/* eslint-disable no-underscore-dangle */
import BaseTable from 'packages/tables/BaseTable';
import {useContext, useEffect, useState} from 'react';
import {TOURNAMENT_LIST_TABLE_HEADERS} from 'packages/constants/table-constants';
import {GET_TOURNAMENTS} from 'packages/graphql/v2/queries';
import {GetTournamentsData} from 'packages/common-types/queries-types';
import {useQuery, useMutation} from '@apollo/client';
import {Alert, Skeleton} from '@material-ui/lab';
import {Button, MenuItem, TableCell, TableRow} from '@material-ui/core';
import TableMenu from 'packages/common-ui/TableMenu';
import {Tournament} from 'packages/common-types/schema-types';
import {PRE_REGISTER} from 'packages/graphql/v2/mutations';
import {AuthContext} from 'packages/context/AuthContext';
import ConfirmAction from 'packages/dialogs/ConfirmAction';
import Swal from 'sweetalert2';
import {useModal} from 'packages/hooks/use-modal';
import InscriptionList from 'packages/dialogs/InscriptionList';
import EmptyComponent from 'packages/common-ui/EmptyComponent';

type PreInscriptionVars = {
  uid: string;
  tournamentId: string;
};

type PreInscriptionData = {
  preRegister: Tournament;
};

const ActiveTournaments = () => {
  const {data, loading, error, refetch} =
    useQuery<GetTournamentsData>(GET_TOURNAMENTS);
  const auth = useContext(AuthContext);
  const [selectedTournament, setSelectedTournament] =
    useState<Tournament | null>(null);
  const userId = auth.user?.uid;
  const [preRegister, {data: preRegisterData, loading: loadingPreRegister}] =
    useMutation<PreInscriptionData, PreInscriptionVars>(PRE_REGISTER, {
      refetchQueries: [{query: GET_TOURNAMENTS}],
    });
  const {modalOpen, toggle} = useModal();
  const {
    modalOpen: modalUserInscriptions,
    toggle: toggleModalUserInscriptions,
  } = useModal();

  const handlePreRegister = () => {
    toggle();
    preRegister({
      variables: {
        uid: userId,
        tournamentId: selectedTournament?._id,
      },
    });
  };

  const handleOpenConfirmModal = (tournament: Tournament) => {
    setSelectedTournament(tournament);
    toggle();
  };

  const handleOpenInscriptionsModal = (tournament: Tournament) => {
    setSelectedTournament(tournament);
    toggleModalUserInscriptions();
  };

  useEffect(() => {
    if (preRegisterData && preRegisterData.preRegister?._id) {
      Swal.fire({
        title: '¡Listo!',
        text: 'Te has pre-inscrito correctamente, no olvides asistir el día del evento para pagar y recibir tu escarapela',
        icon: 'success',
      });
    }
  }, [preRegisterData]);

  if (!data || loading) {
    return (
      <div style={{marginTop: 30}}>
        <Skeleton variant="rect" height={38} />
        <Skeleton variant="rect" height={38} />
        <Skeleton variant="rect" height={38} />
        <Skeleton variant="rect" height={38} />
        <Skeleton variant="rect" height={38} />
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Hubo un problema
        <Button variant="text" color="secondary" onClick={() => refetch()}>
          Cargar de nuevo
        </Button>
      </Alert>
    );
  }

  const renderTournaments = () => {
    if (data.getTournaments.length === 0) {
      return (
        <EmptyComponent message="No hay torneos activos en la Fundación Full Dominó Colombia" />
      );
    }
    return data?.getTournaments.map((tournament) => {
      const {
        _id: id,
        conditions,
        hasFinal,
        name,
        status,
        inscriptions,
      } = tournament;
      const isInTournament = inscriptions.find(
        (inscription) => inscription?.user?.uid === userId
      );
      return (
        <TableRow key={id}>
          <TableCell component="th" scope="row">
            {name}
          </TableCell>
          <TableCell align="right">{conditions.tournamentType}</TableCell>
          <TableCell align="right">{conditions.gender}</TableCell>
          <TableCell align="right">{conditions.modalityGame}</TableCell>
          <TableCell align="right">{conditions.tournamentCategory}</TableCell>
          <TableCell align="right">{conditions.numberOfRounds}</TableCell>
          <TableCell align="right">{conditions.pointLimit}</TableCell>
          <TableCell align="right">{hasFinal ? 'Si' : 'No'}</TableCell>
          <TableCell align="right">{status}</TableCell>
          <TableCell align="right">{inscriptions.length}</TableCell>
          <TableCell align="right">
            <TableMenu title="Inscribete">
              <MenuItem
                id="btn-inscribir"
                disabled={status === 'Iniciado' || !!isInTournament}
                onClick={() => handleOpenConfirmModal(tournament)}
              >
                Inscribirse
              </MenuItem>
              <MenuItem
                disabled={status === 'Iniciado'}
                onClick={() => handleOpenInscriptionsModal(tournament)}
              >
                Ver inscritos
              </MenuItem>
            </TableMenu>
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <>
      <InscriptionList
        inscriptions={selectedTournament?.inscriptions}
        modalOpen={modalUserInscriptions}
        toggle={toggleModalUserInscriptions}
      />
      <ConfirmAction
        toggle={toggle}
        modalOpen={modalOpen}
        onConfirm={handlePreRegister}
        loading={loadingPreRegister}
        title="Confirma ingresar al torneo"
        description={`Estas a punto de inscribirte a ${selectedTournament?.name}, recuerda que debes pagar el día del torneo para recibir tu escarapela`}
      />
      <BaseTable headers={TOURNAMENT_LIST_TABLE_HEADERS}>
        {renderTournaments()}
      </BaseTable>
    </>
  );
};

export default ActiveTournaments;
